import { createSlice } from '@reduxjs/toolkit';
import type { AttendanceState } from '../types/attendanceTypes';
import {
  checkIn,
  checkOut,
  fetchMyAttendance,
  fetchMyCalendar,
  requestLeave,
  fetchMyLeaveRequests,
  fetchMyLeaveBalance,
  fetchLeaveTypes,
  fetchTeamAttendance,
  fetchTeamLeaveRequests,
  reviewLeaveRequest,
  fetchAllAttendance,
  fetchAllLeaveRequests,
  fetchMyYearlySummary,
  fetchTeamYearlySummary,
  fetchAllYearlySummary,
} from '../actions/attendanceActions';

const initialState: AttendanceState = {
  todayRecord: null,
  records: [],
  calendar: [],
  leaveTypes: [],
  leaveBalance: [],
  leaveRequests: [],
  teamAttendance: [],
  teamLeaveRequests: [],
  allAttendance: [],
  totalEmployees: 0,
  allLeaveRequests: [],
  yearlySummary: null,
  teamYearlySummary: [],
  yearlySummaryLoading: false,
  loading: false,
  error: null,
};

const attendanceSlice = createSlice({
  name: 'attendance',
  initialState,
  reducers: {
    clearAttendanceError(state) {
      state.error = null;
    },
  },
  extraReducers: builder => {
    const pending = (state: AttendanceState) => {
      state.loading = true;
      state.error = null;
    };
    const rejected = (state: AttendanceState, action: any) => {
      state.loading = false;
      state.error = action.payload ?? 'Unknown error';
    };

    builder
      .addCase(checkIn.pending, pending)
      .addCase(checkIn.fulfilled, (state, action) => {
        state.loading = false;
        state.todayRecord = action.payload;
      })
      .addCase(checkIn.rejected, rejected)

      .addCase(checkOut.pending, pending)
      .addCase(checkOut.fulfilled, (state, action) => {
        state.loading = false;
        state.todayRecord = action.payload;
      })
      .addCase(checkOut.rejected, rejected)

      .addCase(fetchMyAttendance.pending, pending)
      .addCase(fetchMyAttendance.fulfilled, (state, action) => {
        state.loading = false;
        state.records = action.payload;
      })
      .addCase(fetchMyAttendance.rejected, rejected)

      .addCase(fetchMyCalendar.pending, pending)
      .addCase(fetchMyCalendar.fulfilled, (state, action) => {
        state.loading = false;
        state.calendar = action.payload;
      })
      .addCase(fetchMyCalendar.rejected, rejected)

      .addCase(requestLeave.pending, pending)
      .addCase(requestLeave.fulfilled, (state, action) => {
        state.loading = false;
        state.leaveRequests = [action.payload, ...state.leaveRequests];
      })
      .addCase(requestLeave.rejected, rejected)

      .addCase(fetchMyLeaveRequests.pending, pending)
      .addCase(fetchMyLeaveRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.leaveRequests = action.payload;
      })
      .addCase(fetchMyLeaveRequests.rejected, rejected)

      .addCase(fetchMyLeaveBalance.pending, pending)
      .addCase(fetchMyLeaveBalance.fulfilled, (state, action) => {
        state.loading = false;
        state.leaveBalance = action.payload;
      })
      .addCase(fetchMyLeaveBalance.rejected, rejected)

      .addCase(fetchLeaveTypes.pending, pending)
      .addCase(fetchLeaveTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.leaveTypes = action.payload;
      })
      .addCase(fetchLeaveTypes.rejected, rejected)

      .addCase(fetchTeamAttendance.pending, pending)
      .addCase(fetchTeamAttendance.fulfilled, (state, action) => {
        state.loading = false;
        state.teamAttendance = action.payload;
      })
      .addCase(fetchTeamAttendance.rejected, rejected)

      .addCase(fetchTeamLeaveRequests.pending, pending)
      .addCase(fetchTeamLeaveRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.teamLeaveRequests = action.payload;
      })
      .addCase(fetchTeamLeaveRequests.rejected, rejected)

      .addCase(reviewLeaveRequest.pending, pending)
      .addCase(reviewLeaveRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.teamLeaveRequests = state.teamLeaveRequests.map(r =>
          r.id === action.payload.id ? { ...r, ...action.payload } : r
        );
      })
      .addCase(reviewLeaveRequest.rejected, rejected)

      .addCase(fetchAllAttendance.pending, pending)
      .addCase(fetchAllAttendance.fulfilled, (state, action) => {
        state.loading = false;
        state.allAttendance = action.payload.records;
        state.totalEmployees = action.payload.totalEmployees;
      })
      .addCase(fetchAllAttendance.rejected, rejected)

      .addCase(fetchAllLeaveRequests.pending, pending)
      .addCase(fetchAllLeaveRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.allLeaveRequests = action.payload;
      })
      .addCase(fetchAllLeaveRequests.rejected, rejected)

      .addCase(fetchMyYearlySummary.pending, state => { state.yearlySummaryLoading = true; })
      .addCase(fetchMyYearlySummary.fulfilled, (state, action) => {
        state.yearlySummaryLoading = false;
        state.yearlySummary = action.payload;
      })
      .addCase(fetchMyYearlySummary.rejected, state => { state.yearlySummaryLoading = false; })

      .addCase(fetchTeamYearlySummary.pending, state => { state.yearlySummaryLoading = true; })
      .addCase(fetchTeamYearlySummary.fulfilled, (state, action) => {
        state.yearlySummaryLoading = false;
        state.teamYearlySummary = action.payload;
      })
      .addCase(fetchTeamYearlySummary.rejected, state => { state.yearlySummaryLoading = false; })

      .addCase(fetchAllYearlySummary.pending, state => { state.yearlySummaryLoading = true; })
      .addCase(fetchAllYearlySummary.fulfilled, (state, action) => {
        state.yearlySummaryLoading = false;
        state.teamYearlySummary = action.payload.data;
      })
      .addCase(fetchAllYearlySummary.rejected, state => { state.yearlySummaryLoading = false; });
  },
});

export const { clearAttendanceError } = attendanceSlice.actions;
export default attendanceSlice.reducer;
